/* eslint-disable no-param-reassign */

// Install BootstrapVue
import { BootstrapVue, IconsPlugin } from 'bootstrap-vue';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';

// Axios
import axios from 'axios';

// Moment
import moment from 'moment';

// Filters
import filters from '@/filters/filters';

// VeeValidate
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { extend } from 'vee-validate';
import { required, email, numeric } from 'vee-validate/dist/rules';

// Bank Account validators
import IBAN from 'iban';
import BIC from 'bic';
import ISINValidator from 'isin-validator';
import isoCountries from 'i18n-iso-countries';

// Monthly picker
import VueMonthlyPicker from 'vue-monthly-picker';

// Custom services
import authService from '@/services/auth.service';
import userService from '@/services/users.service';
import adminusersService from '@/services/adminusers.service';
import intervenerService from '@/services/interveners.service';
import accountService from '@/services/accounts.service';
import productsService from '@/services/products.service';
import operationsService from '@/services/operations.service';
import documentsService from '@/services/documents.service';
import publicService from '@/services/onboarding.service';
import issuesService from '@/services/issues.service';

// Custom components
import components from '../../components/components';

const APP_LOG_PREFIX = '[PrivateArea]';

const TIN_GENERIC_ERROR_MESSAGE = 'El número de identificación fiscal tiene un formato incorrecto';

/**
 * Mensajes específicos según el motivo de rechazo (`rejectionReason`) que devuelve el back.
 * Para 'taxpayer-type-mismatch' el mensaje depende del tipo de contribuyente al que
 * corresponde realmente el TIN (`identifierType`).
 */
const TIN_REJECTION_MESSAGES = {
    placeholder:
        'El número de identificación fiscal no puede ser un valor de relleno (todo ceros, un mismo carácter repetido o una secuencia como 12345678)',
    'taxpayer-type-mismatch': {
        individual: 'El número de identificación fiscal corresponde a una persona física',
        entity: 'El número de identificación fiscal corresponde a una persona jurídica'
    }
};

/**
 * Traduce la respuesta del validador de TIN al resultado de una regla de vee-validate:
 * true si es válido, o un string con el mensaje de error a mostrar si no lo es
 * (vee-validate v3 trata un string devuelto por `validate` como regla fallida y lo usa
 * como mensaje de error).
 * @param {Object} data `data` de la respuesta del validador
 * @return {Boolean|String} true si el TIN es válido o el mensaje de error
 */
const getTinValidationResult = data => {
    if (data && data.isTinValid) {
        return true;
    }
    const rejectionReason = data && data.rejectionReason;
    if (rejectionReason === 'placeholder') {
        return TIN_REJECTION_MESSAGES.placeholder;
    }
    if (rejectionReason === 'taxpayer-type-mismatch') {
        return (
            TIN_REJECTION_MESSAGES['taxpayer-type-mismatch'][data.identifierType] ||
            TIN_GENERIC_ERROR_MESSAGE
        );
    }
    return TIN_GENERIC_ERROR_MESSAGE;
};

/**
 * Comprueba contra el back si un TIN es válido para un país.
 * Nunca rechaza: si la llamada falla (el validador externo responde 4xx cuando el TIN no es
 * correcto) vee-validate dejaría el campo en "pending" para siempre —spinner infinito, sin
 * mensaje de error y sin marcar el campo como inválido—, así que cualquier fallo se traduce
 * a "no válido" y el usuario ve el mensaje genérico de la regla.
 * @param {String} countryCode Código de país alpha-2
 * @param {String} tinNumber Identificación fiscal a validar
 * @param {String} [taxpayerType] Tipo de contribuyente ('F' física / 'J' jurídica); si no se
 * indica no se envía y el back valida el TIN para cualquier tipo
 * @return {Promise<Boolean|String>} true si el TIN es válido o el mensaje de error a mostrar
 */
const isTinValid = (countryCode, tinNumber, taxpayerType) =>
    intervenerService
        .validateTinNumber({}, countryCode, tinNumber, taxpayerType)
        .then(response => getTinValidationResult(response && response.data))
        .catch(error => {
            console.warn(`${APP_LOG_PREFIX} No se ha podido validar el TIN`, error);
            return TIN_GENERIC_ERROR_MESSAGE;
        });

const PrivateAreaCore = {
    install(Vue) {
        console.info('- Installing Private Area plugin...');

        // Bootstrap Vue
        Vue.use(BootstrapVue);
        Vue.use(IconsPlugin); // Opcional

        // VeeValidate
        Vue.component('ValidationProvider', ValidationProvider);
        Vue.component('ValidationObserver', ValidationObserver);

        // Monthly picker
        Vue.component('month-picker', VueMonthlyPicker);

        // No message specified.
        extend('email', {
            ...email,
            message: '"{_field_}" tiene un formato incorrecto.'
        });

        // // Override the default message.
        extend('required', {
            ...required,
            message: 'Campo requerido.'
        });

        extend('numeric', {
            ...numeric,
            message: '"{_field_}" debe ser un valor numérico.'
        });

        extend('decimal', {
            validate(value) {
                return value.match(/^\d+[\d,.]*\d+$/g);
            },
            message: '"{_field_}" no contiene un valor válido.'
        });
        extend('decimalPoint', {
            validate(value) {
                return value.match(/^\d*[\d.]*\d+$/g);
            },
            message: '"{_field_}" no contiene un valor válido.'
        });

        extend('min', {
            validate(value, args) {
                return value.length >= args.length;
            },
            params: ['length'],
            message: '"{_field_}" debe tener al menos {length} carácteres.'
        });

        extend('min_value', {
            validate(value, args) {
                return +value >= +args.min;
            },
            params: ['min', 'minFormatted'],
            message: '"{_field_}" debe tener un valor igual o superior a {minFormatted}€'
        });

        extend('min_value_raw', {
            validate(value, args) {
                return +value >= +args.min;
            },
            params: ['min'],
            message: '"{_field_}" debe tener un valor igual o superior a {min}'
        });

        extend('min_value_participations', {
            validate(value, args) {
                return +value >= +args.min;
            },
            params: ['min'],
            message: '"{_field_}" debe tener un valor igual o superior a {min}'
        });

        extend('max_value_currency', {
            validate(value, args) {
                let limit;
                // si tiene decimal se incluye
                if (args.max[1]) limit = `${args.max[0].replaceAll('.', '')}.${args.max[1]}`;
                else limit = args.max[0].replaceAll('.', '');
                limit = parseFloat(limit.replace(',', '.'));
                return +value <= +limit;
            },
            params: ['max'],
            message: (fieldName, placeholders) => {
                if (Array.isArray(placeholders.max)) {
                    if (placeholders.max[0] === '0')
                        return 'Ha llegado a su límite anual para este tipo de operación';
                    if (!Number(placeholders.max[0])) return '';
                    if (placeholders.max[1])
                        return `${fieldName} debe tener un valor igual o menor a ${placeholders.max[0]}.${placeholders.max[1]}€`;
                    return `${fieldName} debe tener un valor igual o menor a ${placeholders.max[0]}€`;
                } else {
                    if (placeholders.max === '0')
                        return 'Ha llegado a su límite anual para este tipo de operación';
                    if (!Number(placeholders.max)) return '';
                }
            }
        });

        extend('max_tutor_anual', {
            validate(value, args) {
                const limit = Number(args.max);
                const amount = Number(value || 0);

                return amount <= limit;
            },

            params: ['max'],

            message: (fieldName, placeholders) => {
                const max = placeholders.max;

                // límite alcanzado
                if (max === '0' || max === 0) {
                    return 'Supera el límite anual de aportación desde una cuenta bancaria asociada a un tutor';
                }

                // evitar mensajes raros
                if (!Number(max)) return '';

                return `El valor debe ser igual o menor a ${max}€ (1200€ de límite anual para cuentas bancarias de tutores)`;
            }
        });

        const parseDate = (d, fallback) => {
            const date = d ? new Date(d) : null;
            return date && !isNaN(date.getTime()) ? date : fallback;
        };

        extend('max_tutor_anual_periodic', {
            validate(value, args) {
                const limit = Number(args.max);
                const periodicity = args.periodicity;
                const startDateRaw = args.start_date;
                const endDateRaw = args.end_date;

                if (
                    limit === 'undefined' ||
                    periodicity === 'undefined' ||
                    startDateRaw === 'undefined'
                ) {
                    return true;
                }

                const monthsStepMap = {
                    MENSUAL: 1,
                    TRIMESTRAL: 3,
                    SEMESTRAL: 6,
                    ANUAL: 12,
                    DEFAULT: 12
                };

                const stepMonths = monthsStepMap[periodicity] || 12;

                const currentYear = new Date().getFullYear();
                const startOfYear = new Date(currentYear, 0, 1);
                const endOfYear = new Date(currentYear, 11, 31);

                let startDate = parseDate(startDateRaw, startOfYear);
                let endDate = parseDate(endDateRaw, endOfYear);

                // limitar al año actual
                if (startDate < startOfYear) startDate = startOfYear;
                if (endDate > endOfYear) endDate = endOfYear;

                if (startDate > endDate) return true;

                // normalizar a inicio de mes (clave para evitar bugs)
                startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

                // cálculo diff de meses
                const diffMonths =
                    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                    (endDate.getMonth() - startDate.getMonth());

                const count = Math.floor(diffMonths / stepMonths) + 1;

                const yearlyAmount = Number(value || 0) * count;

                return yearlyAmount <= limit;
            },

            params: ['max', 'periodicity', 'start_date', 'end_date'],

            message: (fieldName, placeholders) => {
                if (Array.isArray(placeholders.max)) {
                    if (placeholders.max[0] === '0') {
                        return 'Supera el límite anual de aportación desde una cuenta bancaria asociada a un tutor';
                    }

                    return 'Supera el límite anual permitido (1200€) teniendo en cuenta la periodicidad';
                }

                return 'Supera el límite anual de aportación desde una cuenta bancaria asociada a un tutor';
            }
        });

        extend('max_value', {
            validate(value, args) {
                return +value <= +args.max;
            },
            params: ['max'],
            message: '"{_field_}" debe tener un valor igual o menor a {max}€'
        });

        extend('max_value_raw', {
            validate(value, args) {
                return +value <= +args.max;
            },
            params: ['max'],
            message: '"{_field_}" debe tener un valor igual o menor a {max}'
        });

        extend('max_value_participations', {
            validate(value, args) {
                return +value <= +args.max;
            },
            params: ['max'],
            message: '"{_field_}" debe tener un valor igual o menor a {max}'
        });

        extend('max', {
            validate(value, args) {
                return value.length <= args.length;
            },
            params: ['length'],
            message: '"{_field_}" debe tener máximo {length} carácteres.'
        });

        extend('cif', {
            validate(value) {
                const regex = /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/;
                return regex.test(value);
            },
            message: '"{_field_}" tiene un formato incorrecto.'
        });

        extend('dni', {
            validate(value) {
                return validateDNI(value);
            },
            message: '"{_field_}" tiene un formato incorrecto.'
        });

        extend('password', {
            params: ['target'],
            validate(value, { target }) {
                return value === target;
            },
            message: 'Las contraseñas no coinciden'
        });

        extend('non_numeric', {
            validate(value) {
                return !value.match(/\d+/g);
            },
            message: '"{_field_}" no puede contener números.'
        });

        extend('only_letters_numbers', {
            validate(value) {
                return !value.match(/[^A-Za-z0-9\s]+/g);
            },
            message: '"{_field_}" sólo puede contener letras y números.'
        });

        extend('alphanumeric', {
            validate(value) {
                return !value.match(/[^A-Za-zÀ-ÿ\u00f1\u00d10-9\s]+/g);
            },
            message: '"{_field_}" sólo puede contener letras y números.'
        });

        extend('only_letters', {
            validate(value) {
                return !value.match(/[^A-Za-zÀ-ÿ\u00f1\u00d1\s]+/g);
            },
            message: '"{_field_}" sólo puede contener letras.'
        });

        extend('only_letters_dash', {
            validate(value) {
                return value.match(
                    /^[A-Za-zÀ-ÿ\u00f1\u00d1]*[A-Za-zÀ-ÿ\u00f1\u00d1\s\-_]*[A-Za-zÀ-ÿ\u00f1\u00d1]$/g
                );
            },
            message:
                '"{_field_}" sólo puede contener letras y no deje espacios al inicio y final del texto.'
        });

        extend('exact_not_convenient', {
            validate(value) {
                return /^este producto es complejo y se considera no conveniente para m[ií]$/i.test(
                    value.trim()
                );
            },
            message:
                'Debe escribir exactamente: «Este producto es complejo y se considera no conveniente para mí»'
        });

        extend('passport', {
            validate(value) {
                return value.match(/^[A-Z]{3}[0-9]{6}[A-Z]?$/);
            },
            message: '"{_field_}" tiene un formato incorrecto.'
        });

        extend('isin', {
            validate(isin) {
                const validationError = ISINValidator(isin);
                if (!validationError) {
                    return true;
                } else {
                    return false;
                }
            },
            message: '"{_field_}" tiene un formato incorrecto.'
        });

        extend('dgs-plan', {
            validate(value) {
                // Regular expression to check if the value is exactly 5 alphanumeric characters
                const regex = /^[Nn][a-zA-Z0-9]{4}$/;
                return regex.test(value);
            },
            message: 'El Código DGS del plan debe empezar por N y tener 5 caracteres alfanuméricos.'
        });

        extend('dgs-gestora', {
            validate(value) {
                // Regular expression to check if the value is exactly 5 alphanumeric characters
                const regex = /^[Gg][a-zA-Z0-9]{4}$/;
                return regex.test(value);
            },
            message:
                'El Código DGS de la gestora debe empezar por G y tener 5 caracteres alfanuméricos.'
        });

        extend('gestora_comercializador', {
            validate(value) {
                // Regular expression to check if the value is exactly 12 alphanumeric characters
                const regex = /^[a-zA-Z0-9]{12}$/;
                return regex.test(value);
            },
            message: 'El Gestora/Comercializador debe ser de 12 caracteres alfanuméricos.'
        });

        extend('iban', {
            validate(value) {
                return IBAN.isValid(value) && value.match(/^[a-zA-Z0-9]+$/) !== null;
            },
            message: '"{_field_}" tiene un formato incorrecto'
        });

        extend('bic', {
            validate(value) {
                return BIC.isValid(value);
            },
            message: '"{_field_}" tiene un formato incorrecto'
        });

        // Uso: tin-validator:@campo_pais[,tipo] con tipo 'F' (física) o 'J' (jurídica) opcional
        extend('tin-validator', {
            params: ['country', 'taxpayerType'],
            validate(value, { country, taxpayerType }) {
                // El país puede llegar como objeto del multiselect ({ key, name }) o como la
                // clave suelta si el dato viene precargado del back sin normalizar
                const countryKey = typeof country === 'string' ? country : country && country.key;
                // Sin país no se puede validar el TIN (p. ej. datos precargados del back con
                // identificación fiscal pero sin país declarado, o país excluido de las
                // opciones). Se devuelve un mensaje propio en lugar de fallar con el genérico:
                // al seleccionar el país el campo se revalida solo contra el back.
                if (!countryKey) {
                    return 'Selecciona primero el país emisor del TIN';
                }
                // countryKey es alpha-3 (p.ej. 'IRL'); el validador de TIN espera alpha-2
                // ('IE'). No se puede derivar con substring(0, 2): 'IRL' daría 'IR', que es
                // el código real de Irán, no el de Irlanda.
                const countryCode =
                    isoCountries.alpha3ToAlpha2(countryKey) || countryKey.substring(0, 2);
                return isTinValid(countryCode, value, taxpayerType);
            },
            message: TIN_GENERIC_ERROR_MESSAGE
        });

        // Uso: fatca-tin-validator[:tipo] con tipo 'F' (física) o 'J' (jurídica) opcional
        extend('fatca-tin-validator', {
            params: ['taxpayerType'],
            validate(value, { taxpayerType }) {
                return isTinValid('US', value, taxpayerType);
            },
            message: TIN_GENERIC_ERROR_MESSAGE
        });

        extend('spanishPhone', {
            validate(value) {
                const firstNumber = +value.charAt(0);
                return firstNumber !== 7 && firstNumber !== 6 ? false : true;
            },
            message: '"{_field_}" debe empezar por 6 o 7.'
        });

        extend('nie', {
            validate(value) {
                return validateNIE(value);
            },
            message: 'El NIE introducido tiene un formato incorrecto.'
        });

        extend('not_intervener_document', {
            validate(value, args) {
                const documentIdsString = Array.isArray(args) && args.length > 0 ? args[0] : '';

                if (!documentIdsString || documentIdsString.trim() === '') {
                    return true;
                }

                const existingDocumentIds = documentIdsString.split(',').map(id => id.trim());

                return !existingDocumentIds.includes(value);
            },
            message:
                'El número de documento del aportante no puede ser el mismo que el de un interviniente de la cuenta'
        });

        extend('periodicity', {
            validate(value, args) {
                const { max, grossAmount, numberOfContributions } = args;

                if (numberOfContributions && !numberOfContributions.includes('undefined')) {
                    return grossAmount > 0 ? +grossAmount * numberOfContributions <= +max : true;
                } else {
                    return true;
                }
            },
            params: ['max', 'grossAmount', 'numberOfContributions', 'max_formatted'],
            message: 'No puede superar la cantidad de {max_formatted}€ anuales.'
        });
        extend('validIncrease', {
            validate(value, args) {
                let increase = +value;
                const { grossAmount, increase_type, max_limit, numberOfContributions } = args;
                if (increase_type === 'PORCENTUAL') {
                    increase = (+grossAmount * +increase) / 100;
                }
                let limit = max_limit[0].replaceAll('.', '');
                limit = parseFloat(limit.replace(',', '.'));
                const increasedContribution = (+grossAmount + +increase) * numberOfContributions;
                return increasedContribution <= +limit;
            },
            params: ['grossAmount', 'increase_type', 'numberOfContributions', 'max_limit'],
            message: "'Superará el máximo de {max_limit}€ anuales con la proxima revalorización'"
        });

        function validateNIE(nie) {
            const NIE_REGEX = /^[XYZ]{1}[0-9]{7,8}[A-Za-z]$/g;
            if (!nie.match(NIE_REGEX, nie)) return false;

            const nie_letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
            const nie_control_digit = nie.substr(nie.length - 1);
            const number = nie
                .substr(0, nie.length - 1)
                .replace('X', '0')
                .replace('Y', '1')
                .replace('Z', '2');
            const valid_control_digit = nie_letters.charAt(parseInt(number, 10) % 23);

            return nie_control_digit === valid_control_digit;
        }

        function validateDNI(dni) {
            const DNI_REGEX = /^[0-9]{8,8}[A-Za-z]$/g;

            if (!dni.match(DNI_REGEX)) return false;

            const dni_letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
            const letter = dni_letters.charAt(parseInt(dni, 10) % 23);

            return letter === dni.charAt(8).toUpperCase();
        }

        // /* Global register all components */
        Object.keys(components).forEach(k => {
            console.log(`🛠%c Componente registrado%c ${k}`, 'color: green', 'font-weight: bold');
            Vue.component(k, components[k]);
        });

        /* Register all filters */
        Object.keys(filters).forEach(k => Vue.filter(k, filters[k]));

        // Axios
        Vue.prototype.$axios = axios;
        Vue.prototype.$authService = authService;
        Vue.prototype.$userService = userService;
        Vue.prototype.$adminusersService = adminusersService;
        Vue.prototype.$intervenerService = intervenerService;
        Vue.prototype.$accountService = accountService;
        Vue.prototype.$productsService = productsService;
        Vue.prototype.$operationsService = operationsService;
        Vue.prototype.$documentsService = documentsService;
        Vue.prototype.$publicService = publicService;
        Vue.prototype.$issuesService = issuesService;
        Vue.prototype.$moment = moment;

        // Moment
        moment.locale('es');
        Vue.filter('moment', function (date, format) {
            return moment(date).format(format);
        });

        Vue.prototype.$log = {
            info: text => console.info(`${APP_LOG_PREFIX} - ${text}`),
            error: text => console.error(`${APP_LOG_PREFIX} - ${text}`),
            debug: text => console.log(`${APP_LOG_PREFIX} - ${text}`),
            warn: text => console.warn(`${APP_LOG_PREFIX} - ${text}`)
        };
    }
};

export default PrivateAreaCore;
